import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Sidebar from './components/Layout/Sidebar';
import Topbar from './components/Layout/Topbar';
import { ThemeProvider } from './assets/ThemeContext';
import { AuthProvider, useAuth } from './assets/AuthContext';
import { SurfaceStyleProvider } from './assets/SurfaceStyleContext';
import ErrorBoundary from './components/ErrorBoundary';

import Dashboard from './pages/Dashboard';
import ProjectsPage from './pages/Projects';
import ProjectPage from './pages/ProjectPage';
import AssessmentPage from './pages/AssessmentPage';
import DataManager from './pages/DataManager';
import Settings from './pages/Settings';
import LoginPage from './pages/LoginPage';
import UserManagement from './pages/UserManagement';

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user || user.role !== 'admin') return <Navigate to="/" replace />;
  return children;
}

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(145deg, #0f172a 0%, #0a1424 100%)',
    }}>
      <div style={{ color: 'rgba(148,168,200,0.5)', fontSize: 13 }}>Loading…</div>
    </div>
  );
  if (!user) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/" replace />;
  return children;
}

function AppShell({ collapsed, setCollapsed }) {
  const SIDEBAR_W = collapsed ? 56 : 220;
  const location = useLocation();
  return (
    <div style={{
      display: 'flex',
      background: 'var(--t-bodyBg)',
      backgroundAttachment: 'fixed',
      minHeight: '100vh',
      height: '100vh',
      overflow: 'hidden',
      color: 'var(--t-white)',
    }}>
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
      <div style={{
        marginLeft: SIDEBAR_W,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        transition: 'margin-left 0.25s ease',
        height: '100vh',
        overflow: 'hidden',
      }}>
        <Topbar sidebarWidth={SIDEBAR_W} />
        <main style={{
          marginTop: 60,
          flex: 1,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}>
          <ErrorBoundary key={location.pathname}>
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/projects" element={<ProjectsPage />} />
              <Route path="/projects/:id" element={<ProjectPage />} />
              <Route path="/projects/:id/assess" element={<AssessmentPage />} />
              <Route path="/data" element={<AdminRoute><DataManager /></AdminRoute>} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/users" element={<AdminRoute><UserManagement /></AdminRoute>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </ErrorBoundary>
        </main>
        <footer style={{
          borderTop: '1px solid var(--glass-border)',
          padding: '8px 24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: 10,
          color: 'var(--text-tertiary)',
          background: 'rgba(8,20,32,0.4)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          flexShrink: 0,
        }}>
          <span>Tsela Obstacle Safeguarding · ICAO Annex 14 (9th Ed 2022) · EUR Doc 015 (3rd Ed 2015) · PANS-OPS Doc 8168</span>
          <span>© 2024 Tsela OAS · South Africa</span>
        </footer>
      </div>
    </div>
  );
}

export default function App() {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <ThemeProvider>
      <AuthProvider>
        <SurfaceStyleProvider>
          <BrowserRouter future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
            <Routes>
              <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
              <Route path="/*" element={
                <PrivateRoute>
                  <AppShell collapsed={collapsed} setCollapsed={setCollapsed} />
                </PrivateRoute>
              } />
            </Routes>
          </BrowserRouter>
        </SurfaceStyleProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}
